import React, { useState, useEffect, useRef } from "react";
import { adminProductService, adminCategoryService } from "../../services/api";
import { Product, Category, ProductPriceInput } from "../../types";
import LoadingSpinner from "../../components/LoadingSpinner";
import Modal from "../../components/Modal";
import { useNotification } from "../../context/NotificationContext";

const API_URL = process.env.REACT_APP_API_URL || "";

const Products: React.FC = () => {
    const notification = useNotification();
    const [products, setProducts] = useState<Product[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState<Product | null>(null);
    const [productToDelete, setProductToDelete] = useState<Product | null>(
        null,
    );
    const [mediaProduct, setMediaProduct] = useState<Product | null>(null);

    const [formName, setFormName] = useState("");
    const [formDescription, setFormDescription] = useState("");
    const [formCategoryId, setFormCategoryId] = useState<number>(0);
    const [formPrice, setFormPrice] = useState("");
    const [formPriceUnit, setFormPriceUnit] = useState<
        "gramme" | "unite" | "kg"
    >("gramme");
    const [formPrices, setFormPrices] = useState<ProductPriceInput[]>([]);
    const [formIsAvailable, setFormIsAvailable] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const [pendingMediaFiles, setPendingMediaFiles] = useState<File[]>([]);
    const createMediaInputRef = useRef<HTMLInputElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isUploading, setIsUploading] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setIsLoading(true);
            const [productsData, categoriesData] = await Promise.all([
                adminProductService.getAll(),
                adminCategoryService.getAll(),
            ]);
            setProducts(productsData);
            setCategories(categoriesData);
        } catch (err) {
            notification.error("Erreur lors du chargement des donnees");
        } finally {
            setIsLoading(false);
        }
    };

    const openCreateModal = () => {
        setEditingProduct(null);
        setFormName("");
        setFormDescription("");
        setFormCategoryId(categories[0]?.id || 0);
        setFormPrice("");
        setFormPriceUnit("gramme");
        setFormPrices([
            {
                label: "",
                price: 0,
                quantity: 1,
                price_unit: "gramme",
                is_active: true,
            },
        ]);
        setFormIsAvailable(true);
        setPendingMediaFiles([]);
        setIsModalOpen(true);
    };

    const openEditModal = (product: Product) => {
        setEditingProduct(product);
        setFormName(product.name);
        setFormDescription(product.description || "");
        setFormCategoryId(product.category_id);
        setFormPrice(product.price.toString());
        setFormPriceUnit(product.price_unit);
        const existingPrices =
            product.prices?.map((p) => ({
                label: p.label,
                price: p.price,
                quantity: p.quantity,
                price_unit: p.price_unit,
                is_active: p.is_active,
            })) || [];
        setFormPrices(
            existingPrices.length > 0
                ? existingPrices
                : [
                      {
                          label: "",
                          price: product.price,
                          quantity: 1,
                          price_unit: product.price_unit,
                          is_active: true,
                      },
                  ],
        );
        setFormIsAvailable(product.is_available);
        setPendingMediaFiles([]);
        setIsModalOpen(true);
    };

    const openDeleteModal = (product: Product) => {
        setProductToDelete(product);
        setIsDeleteModalOpen(true);
    };

    const openMediaModal = (product: Product) => {
        setMediaProduct(product);
        setIsMediaModalOpen(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const data: any = {
                name: formName,
                description: formDescription || undefined,
                category_id: formCategoryId,
                is_available: formIsAvailable,
            };
            if (formPrices.length > 0) {
                data.prices = formPrices;
                const activePrice =
                    formPrices.find((p) => p.is_active) || formPrices[0];
                data.price = activePrice.price;
                data.price_unit = activePrice.price_unit;
            } else {
                data.price = parseFloat(formPrice);
                data.price_unit = formPriceUnit;
            }

            let productId: number;
            if (editingProduct) {
                await adminProductService.update(editingProduct.id, data);
                productId = editingProduct.id;
                notification.success("Produit mis a jour avec succes");
            } else {
                const newProduct = await adminProductService.create(data);
                productId = newProduct.id;
                notification.success("Produit cree avec succes");
            }

            for (const file of pendingMediaFiles) {
                try {
                    await adminProductService.addMedia(productId, file);
                } catch (err) {
                    notification.warning(`Erreur upload: ${file.name}`);
                }
            }

            setIsModalOpen(false);
            loadData();
        } catch (err: any) {
            notification.error(
                err.response?.data?.error || "Erreur lors de la sauvegarde",
            );
        } finally {
            setIsSaving(false);
        }
    };

    const moveProduct = async (
        categoryId: number,
        categoryProducts: Product[],
        index: number,
        direction: -1 | 1,
    ) => {
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= categoryProducts.length) return;

        const reordered = [...categoryProducts];
        [reordered[index], reordered[targetIndex]] = [
            reordered[targetIndex],
            reordered[index],
        ];
        const reorderedIds = reordered.map((p) => p.id);

        setProducts((prev) => {
            const others = prev.filter((p) => p.category_id !== categoryId);
            const updated = reordered.map((p, i) => ({
                ...p,
                display_order: i + 1,
            }));
            return [...others, ...updated];
        });

        try {
            await adminProductService.reorder(categoryId, reorderedIds);
        } catch (err: any) {
            notification.error(
                err.response?.data?.error ||
                    "Erreur lors du changement d'ordre",
            );
            loadData();
        }
    };

    const handleDelete = async () => {
        if (!productToDelete) return;
        setIsSaving(true);
        try {
            await adminProductService.delete(productToDelete.id);
            notification.success("Produit supprime avec succes");
            setIsDeleteModalOpen(false);
            setProductToDelete(null);
            loadData();
        } catch (err: any) {
            notification.error(
                err.response?.data?.error || "Erreur lors de la suppression",
            );
        } finally {
            setIsSaving(false);
        }
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !mediaProduct) return;
        setIsUploading(true);
        try {
            await adminProductService.addMedia(mediaProduct.id, file);
            notification.success("Media ajoute avec succes");
            const updatedProduct = await adminProductService.getById(
                mediaProduct.id,
            );
            setMediaProduct(updatedProduct);
            loadData();
        } catch (err: any) {
            notification.error(
                err.response?.data?.error || "Erreur lors de l'upload",
            );
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const handleDeleteMedia = async (mediaId: number) => {
        if (!mediaProduct) return;
        try {
            await adminProductService.deleteMedia(mediaProduct.id, mediaId);
            notification.success("Media supprime avec succes");
            const updatedProduct = await adminProductService.getById(
                mediaProduct.id,
            );
            setMediaProduct(updatedProduct);
            loadData();
        } catch (err: any) {
            notification.error(
                err.response?.data?.error || "Erreur lors de la suppression",
            );
        }
    };

    const handleSetPrimaryMedia = async (mediaId: number) => {
        if (!mediaProduct) return;
        try {
            await adminProductService.setPrimaryMedia(mediaProduct.id, mediaId);
            notification.success("Media principal defini");
            const updatedProduct = await adminProductService.getById(
                mediaProduct.id,
            );
            setMediaProduct(updatedProduct);
            loadData();
        } catch (err: any) {
            notification.error(err.response?.data?.error || "Erreur");
        }
    };

    const addPrice = () => {
        setFormPrices([
            ...formPrices,
            {
                label: "",
                price: 0,
                quantity: 1,
                price_unit: "gramme",
                is_active: true,
            },
        ]);
    };

    const updatePrice = (
        index: number,
        field: keyof ProductPriceInput,
        value: any,
    ) => {
        const newPrices = [...formPrices];
        newPrices[index] = { ...newPrices[index], [field]: value };
        setFormPrices(newPrices);
    };

    const removePrice = (index: number) => {
        setFormPrices(formPrices.filter((_, i) => i !== index));
    };

    const handleCreateMediaSelect = (
        e: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const files = e.target.files;
        if (!files) return;
        const validFiles: File[] = [];
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const ext = file.name.toLowerCase().split(".").pop();
            if (
                [
                    "jpg",
                    "jpeg",
                    "png",
                    "webp",
                    "gif",
                    "mp4",
                    "webm",
                    "mov",
                ].includes(ext || "")
            ) {
                if (file.size <= 5 * 1024 * 1024 * 1024) {
                    validFiles.push(file);
                } else {
                    notification.warning(
                        `${file.name} trop volumineux (max 5GB)`,
                    );
                }
            } else {
                notification.warning(`${file.name} format non supporte`);
            }
        }
        setPendingMediaFiles([...pendingMediaFiles, ...validFiles]);
        if (createMediaInputRef.current) createMediaInputRef.current.value = "";
    };

    const removePendingMedia = (index: number) => {
        setPendingMediaFiles(pendingMediaFiles.filter((_, i) => i !== index));
    };

    const unitLabels: Record<string, string> = {
        gramme: "g",
        unite: "u",
        kg: "kg",
    };

    const formatPriceWithQuantity = (
        price: number,
        quantity: number,
        unit: string,
    ) => {
        const qty = quantity > 0 ? quantity : 1;
        return `${price.toFixed(2)} EUR / ${qty}${unitLabels[unit] || unit}`;
    };

    const getProductDisplayPrice = (product: Product) => {
        const activePrices = product.prices?.filter((p) => p.is_active) || [];
        if (activePrices.length > 0) {
            const firstPrice = activePrices[0];
            return formatPriceWithQuantity(
                firstPrice.price,
                firstPrice.quantity,
                firstPrice.price_unit,
            );
        }
        return `${product.price.toFixed(2)} EUR / ${unitLabels[product.price_unit] || product.price_unit}`;
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <LoadingSpinner size="lg" text="Chargement des produits..." />
            </div>
        );
    }

    return (
        <div className="min-h-screen py-8 px-4">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-white mb-2">
                            Gestion des Produits
                        </h1>
                        <p className="text-gray-400">
                            {products.length} produit(s) au total
                        </p>
                    </div>
                    <button
                        onClick={openCreateModal}
                        disabled={categories.length === 0}
                        className="btn-primary mt-4 sm:mt-0 disabled:opacity-50"
                    >
                        <span className="flex items-center space-x-2">
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                />
                            </svg>
                            <span>Nouveau produit</span>
                        </span>
                    </button>
                </div>

                {categories.length === 0 && (
                    <div className="mb-6 p-4 bg-cyan-500/20 border border-cyan-500/50 rounded-lg text-cyan-400">
                        Veuillez creer au moins une categorie avant d'ajouter
                        des produits.
                    </div>
                )}

                {/* Products Grid */}
                {products.length === 0 ? (
                    <div className="card p-12 text-center">
                        <svg
                            className="w-16 h-16 mx-auto text-gray-600 mb-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                            />
                        </svg>
                        <p className="text-gray-400 text-lg mb-4">
                            Aucun produit pour le moment
                        </p>
                        {categories.length > 0 && (
                            <button
                                onClick={openCreateModal}
                                className="btn-primary"
                            >
                                Creer un produit
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="space-y-10">
                        {categories.map((category) => {
                            const categoryProducts = products
                                .filter(
                                    (p) => p.category_id === category.id,
                                )
                                .sort(
                                    (a, b) =>
                                        a.display_order - b.display_order,
                                );
                            if (categoryProducts.length === 0) return null;

                            return (
                                <div key={category.id}>
                                    <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                        {category.name}
                                        <span className="text-sm font-normal text-gray-400">
                                            ({categoryProducts.length})
                                        </span>
                                    </h2>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {categoryProducts.map(
                                            (product, index) => {
                                                // Priorise image primary > toute image > vidéo primary > toute vidéo
                                                const primaryMedia =
                                                    product.media?.find(
                                                        (m) =>
                                                            m.is_primary &&
                                                            m.media_type ===
                                                                "image",
                                                    ) ??
                                                    product.media?.find(
                                                        (m) =>
                                                            m.media_type ===
                                                            "image",
                                                    ) ??
                                                    product.media?.find(
                                                        (m) => m.is_primary,
                                                    ) ??
                                                    product.media?.[0];
                                                const mediaUrl = primaryMedia
                                                    ? `${API_URL}/${primaryMedia.media_path}`
                                                    : "/images/placeholder.jpg";
                                                const isVideo =
                                                    primaryMedia?.media_type ===
                                                    "video";

                                                return (
                                                    <div
                                                        key={product.id}
                                                        className="card overflow-hidden"
                                                    >
                                                        <div className="relative aspect-video">
                                                            {isVideo ? (
                                                                <video
                                                                    src={
                                                                        mediaUrl
                                                                    }
                                                                    className="w-full h-full object-cover"
                                                                    muted
                                                                    autoPlay
                                                                    loop
                                                                    playsInline
                                                                />
                                                            ) : (
                                                                <img
                                                                    src={
                                                                        mediaUrl
                                                                    }
                                                                    alt={
                                                                        product.name
                                                                    }
                                                                    className="w-full h-full object-cover"
                                                                    onError={(
                                                                        e,
                                                                    ) => {
                                                                        (
                                                                            e.target as HTMLImageElement
                                                                        ).src =
                                                                            "/images/placeholder.jpg";
                                                                    }}
                                                                />
                                                            )}
                                                            {!product.is_available && (
                                                                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                                                    <span className="text-white font-semibold">
                                                                        Indisponible
                                                                    </span>
                                                                </div>
                                                            )}
                                                            <div className="absolute top-2 left-2 flex items-center gap-1 bg-primary-dark/80 rounded-lg p-0.5">
                                                                <button
                                                                    onClick={() =>
                                                                        moveProduct(
                                                                            category.id,
                                                                            categoryProducts,
                                                                            index,
                                                                            -1,
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        index ===
                                                                        0
                                                                    }
                                                                    className="p-1 text-gray-300 hover:text-white hover:bg-white/10 rounded transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
                                                                    title="Monter"
                                                                >
                                                                    <svg
                                                                        className="w-4 h-4"
                                                                        fill="none"
                                                                        viewBox="0 0 24 24"
                                                                        stroke="currentColor"
                                                                    >
                                                                        <path
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                            strokeWidth={
                                                                                2
                                                                            }
                                                                            d="M5 15l7-7 7 7"
                                                                        />
                                                                    </svg>
                                                                </button>
                                                                <button
                                                                    onClick={() =>
                                                                        moveProduct(
                                                                            category.id,
                                                                            categoryProducts,
                                                                            index,
                                                                            1,
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        index ===
                                                                        categoryProducts.length -
                                                                            1
                                                                    }
                                                                    className="p-1 text-gray-300 hover:text-white hover:bg-white/10 rounded transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
                                                                    title="Descendre"
                                                                >
                                                                    <svg
                                                                        className="w-4 h-4"
                                                                        fill="none"
                                                                        viewBox="0 0 24 24"
                                                                        stroke="currentColor"
                                                                    >
                                                                        <path
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                            strokeWidth={
                                                                                2
                                                                            }
                                                                            d="M19 9l-7 7-7-7"
                                                                        />
                                                                    </svg>
                                                                </button>
                                                            </div>
                                                            <div className="absolute top-2 right-2">
                                                                <span className="bg-primary-dark/90 text-white text-xs px-2 py-1 rounded">
                                                                    {product
                                                                        .media
                                                                        ?.length ||
                                                                        0}{" "}
                                                                    media(s)
                                                                </span>
                                                            </div>
                                                        </div>

                                                        <div className="p-4">
                                                            <h3 className="text-white font-semibold text-lg mb-1">
                                                                {product.name}
                                                            </h3>
                                                            <p className="text-accent font-bold mb-3">
                                                                {getProductDisplayPrice(
                                                                    product,
                                                                )}
                                                            </p>
                                                            <div className="flex flex-wrap gap-2">
                                                                <button
                                                                    onClick={() =>
                                                                        openEditModal(
                                                                            product,
                                                                        )
                                                                    }
                                                                    className="flex-1 text-sm bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 py-2 px-3 rounded-lg transition-colors"
                                                                >
                                                                    Modifier
                                                                </button>
                                                                <button
                                                                    onClick={() =>
                                                                        openMediaModal(
                                                                            product,
                                                                        )
                                                                    }
                                                                    className="flex-1 text-sm bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 py-2 px-3 rounded-lg transition-colors"
                                                                >
                                                                    Medias
                                                                </button>
                                                                <button
                                                                    onClick={() =>
                                                                        openDeleteModal(
                                                                            product,
                                                                        )
                                                                    }
                                                                    className="text-sm bg-red-500/20 text-red-400 hover:bg-red-500/30 py-2 px-3 rounded-lg transition-colors"
                                                                >
                                                                    <svg
                                                                        className="w-5 h-5"
                                                                        fill="none"
                                                                        viewBox="0 0 24 24"
                                                                        stroke="currentColor"
                                                                    >
                                                                        <path
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                            strokeWidth={
                                                                                2
                                                                            }
                                                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                                        />
                                                                    </svg>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            },
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Create/Edit Modal */}
                <Modal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    title={
                        editingProduct
                            ? "Modifier le produit"
                            : "Nouveau produit"
                    }
                    size="2xl"
                >
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="md:col-span-2">
                                <label
                                    htmlFor="name"
                                    className="block text-sm font-medium text-gray-300 mb-2"
                                >
                                    Nom *
                                </label>
                                <input
                                    id="name"
                                    type="text"
                                    value={formName}
                                    onChange={(e) =>
                                        setFormName(e.target.value)
                                    }
                                    className="input-field"
                                    placeholder="Nom du produit"
                                    required
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor="category"
                                    className="block text-sm font-medium text-gray-300 mb-2"
                                >
                                    Categorie *
                                </label>
                                <select
                                    id="category"
                                    value={formCategoryId}
                                    onChange={(e) =>
                                        setFormCategoryId(
                                            parseInt(e.target.value),
                                        )
                                    }
                                    className="input-field"
                                    required
                                >
                                    {categories.map((cat) => (
                                        <option key={cat.id} value={cat.id}>
                                            {cat.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex items-center">
                                <label className="flex items-center space-x-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formIsAvailable}
                                        onChange={(e) =>
                                            setFormIsAvailable(e.target.checked)
                                        }
                                        className="w-5 h-5 rounded border-gray-600 bg-primary-dark text-accent focus:ring-accent"
                                    />
                                    <span className="text-gray-300">
                                        Produit disponible
                                    </span>
                                </label>
                            </div>
                            <div className="md:col-span-2">
                                <label
                                    htmlFor="description"
                                    className="block text-sm font-medium text-gray-300 mb-2"
                                >
                                    Description
                                </label>
                                <textarea
                                    id="description"
                                    value={formDescription}
                                    onChange={(e) =>
                                        setFormDescription(e.target.value)
                                    }
                                    className="input-field min-h-[80px] resize-none"
                                    placeholder="Description du produit (optionnelle)"
                                />
                            </div>
                        </div>

                        {/* Prix */}
                        <div className="border-t border-white/10 pt-4">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-medium text-white">
                                    Prix
                                </h3>
                                <button
                                    type="button"
                                    onClick={addPrice}
                                    className="text-sm text-accent hover:text-accent-light transition-colors"
                                >
                                    + Ajouter un prix
                                </button>
                            </div>
                            <div className="space-y-3">
                                {formPrices.map((price, index) => (
                                    <div
                                        key={index}
                                        className="p-3 bg-primary-dark/50 rounded-lg"
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm text-gray-400">
                                                Prix {index + 1}
                                            </span>
                                            <div className="flex items-center gap-2">
                                                <label className="flex items-center gap-1 text-sm text-gray-400 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            price.is_active
                                                        }
                                                        onChange={(e) =>
                                                            updatePrice(
                                                                index,
                                                                "is_active",
                                                                e.target
                                                                    .checked,
                                                            )
                                                        }
                                                        className="w-4 h-4 rounded border-gray-600 bg-primary-dark text-accent focus:ring-accent"
                                                    />
                                                    Actif
                                                </label>
                                                {formPrices.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            removePrice(index)
                                                        }
                                                        className="text-red-400 hover:text-red-300 p-1"
                                                    >
                                                        <svg
                                                            className="w-5 h-5"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            stroke="currentColor"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                            />
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div>
                                                <label className="block text-xs text-gray-400 mb-1">
                                                    Prix (EUR)
                                                </label>
                                                <input
                                                    type="text"
                                                    value={price.price || ""}
                                                    onChange={(e) =>
                                                        updatePrice(
                                                            index,
                                                            "price",
                                                            parseFloat(
                                                                e.target.value.replace(
                                                                    ",",
                                                                    ".",
                                                                ),
                                                            ) || 0,
                                                        )
                                                    }
                                                    className="input-field w-24"
                                                    placeholder="0.00"
                                                    required
                                                />
                                            </div>
                                            <span className="text-gray-400 pt-5">
                                                /
                                            </span>
                                            <div>
                                                <label className="block text-xs text-gray-400 mb-1">
                                                    Quantite
                                                </label>
                                                <input
                                                    type="text"
                                                    value={price.quantity || ""}
                                                    onChange={(e) =>
                                                        updatePrice(
                                                            index,
                                                            "quantity",
                                                            parseFloat(
                                                                e.target.value.replace(
                                                                    ",",
                                                                    ".",
                                                                ),
                                                            ) || 1,
                                                        )
                                                    }
                                                    className="input-field w-20"
                                                    placeholder="1"
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs text-gray-400 mb-1">
                                                    Unite
                                                </label>
                                                <select
                                                    value={price.price_unit}
                                                    onChange={(e) =>
                                                        updatePrice(
                                                            index,
                                                            "price_unit",
                                                            e.target.value,
                                                        )
                                                    }
                                                    className="input-field w-20"
                                                >
                                                    <option value="gramme">
                                                        g
                                                    </option>
                                                    <option value="unite">
                                                        u
                                                    </option>
                                                    <option value="kg">
                                                        kg
                                                    </option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Médias */}
                        <div className="border-t border-white/10 pt-4">
                            <h3 className="text-lg font-medium text-white mb-4">
                                Medias
                            </h3>
                            <input
                                ref={createMediaInputRef}
                                type="file"
                                accept="image/*,video/*"
                                multiple
                                onChange={handleCreateMediaSelect}
                                className="hidden"
                            />
                            <button
                                type="button"
                                onClick={() =>
                                    createMediaInputRef.current?.click()
                                }
                                className="w-full border-2 border-dashed border-white/20 hover:border-accent/50 rounded-lg p-4 text-center transition-colors mb-4"
                            >
                                <svg
                                    className="w-6 h-6 mx-auto text-gray-400 mb-1"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                    />
                                </svg>
                                <p className="text-gray-400 text-sm">
                                    Ajouter des images ou videos
                                </p>
                            </button>
                            {pendingMediaFiles.length > 0 && (
                                <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                                    {pendingMediaFiles.map((file, index) => (
                                        <div
                                            key={index}
                                            className="relative group"
                                        >
                                            <div className="aspect-square bg-primary-dark/50 rounded-lg overflow-hidden flex items-center justify-center">
                                                {file.type.startsWith(
                                                    "image/",
                                                ) ? (
                                                    <img
                                                        src={URL.createObjectURL(
                                                            file,
                                                        )}
                                                        alt=""
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <video
                                                        src={URL.createObjectURL(
                                                            file,
                                                        )}
                                                        className="w-full h-full object-cover"
                                                        muted
                                                        autoPlay
                                                        loop
                                                        playsInline
                                                    />
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    removePendingMedia(index)
                                                }
                                                className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                            >
                                                <svg
                                                    className="w-3 h-3"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M6 18L18 6M6 6l12 12"
                                                    />
                                                </svg>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end space-x-3 pt-4 border-t border-white/10">
                            <button
                                type="button"
                                onClick={() => setIsModalOpen(false)}
                                className="btn-secondary"
                            >
                                Annuler
                            </button>
                            <button
                                type="submit"
                                disabled={isSaving}
                                className="btn-primary disabled:opacity-50"
                            >
                                {isSaving
                                    ? "Sauvegarde..."
                                    : editingProduct
                                      ? "Mettre a jour"
                                      : "Creer"}
                            </button>
                        </div>
                    </form>
                </Modal>

                {/* Delete Modal */}
                <Modal
                    isOpen={isDeleteModalOpen}
                    onClose={() => setIsDeleteModalOpen(false)}
                    title="Confirmer la suppression"
                    size="sm"
                >
                    <div className="space-y-4">
                        <p className="text-gray-300">
                            Etes-vous sur de vouloir supprimer le produit{" "}
                            <span className="text-white font-semibold">
                                {productToDelete?.name}
                            </span>{" "}
                            ?
                        </p>
                        <p className="text-red-400 text-sm">
                            Cette action est irreversible.
                        </p>
                        <div className="flex justify-end space-x-3 pt-4">
                            <button
                                onClick={() => setIsDeleteModalOpen(false)}
                                className="btn-secondary"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={isSaving}
                                className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors disabled:opacity-50"
                            >
                                {isSaving ? "Suppression..." : "Supprimer"}
                            </button>
                        </div>
                    </div>
                </Modal>

                {/* Media Modal */}
                <Modal
                    isOpen={isMediaModalOpen}
                    onClose={() => setIsMediaModalOpen(false)}
                    title={`Medias - ${mediaProduct?.name}`}
                    size="xl"
                >
                    <div className="space-y-4">
                        <div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*,video/*"
                                onChange={handleFileUpload}
                                className="hidden"
                            />
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                disabled={isUploading}
                                className="w-full border-2 border-dashed border-white/20 hover:border-accent/50 rounded-lg p-6 text-center transition-colors disabled:opacity-50"
                            >
                                {isUploading ? (
                                    <div className="flex items-center justify-center space-x-2">
                                        <LoadingSpinner size="sm" />
                                        <span className="text-gray-400">
                                            Upload en cours...
                                        </span>
                                    </div>
                                ) : (
                                    <>
                                        <svg
                                            className="w-8 h-8 mx-auto text-gray-400 mb-2"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                            />
                                        </svg>
                                        <p className="text-gray-400">
                                            Cliquez pour ajouter une image ou
                                            video
                                        </p>
                                    </>
                                )}
                            </button>
                        </div>

                        {mediaProduct?.media &&
                        mediaProduct.media.length > 0 ? (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                {mediaProduct.media.map((media) => (
                                    <div
                                        key={media.id}
                                        className={`relative rounded-lg overflow-hidden border-2 ${media.is_primary ? "border-accent" : "border-transparent"}`}
                                    >
                                        {media.media_type === "video" ? (
                                            <video
                                                src={`${API_URL}/${media.media_path}`}
                                                className="w-full aspect-square object-cover"
                                                controls
                                            />
                                        ) : (
                                            <img
                                                src={`${API_URL}/${media.media_path}`}
                                                alt=""
                                                className="w-full aspect-square object-cover"
                                            />
                                        )}
                                        {media.is_primary && (
                                            <div className="absolute top-2 left-2 bg-accent text-white text-xs font-semibold px-2 py-1 rounded">
                                                Principal
                                            </div>
                                        )}
                                        <div className="absolute bottom-0 left-0 right-0 bg-black/80 p-2 flex justify-center space-x-2">
                                            {!media.is_primary && (
                                                <button
                                                    onClick={() =>
                                                        handleSetPrimaryMedia(
                                                            media.id,
                                                        )
                                                    }
                                                    className="text-xs bg-accent/20 text-accent hover:bg-accent/30 px-2 py-1 rounded transition-colors"
                                                >
                                                    Principal
                                                </button>
                                            )}
                                            <button
                                                onClick={() =>
                                                    handleDeleteMedia(media.id)
                                                }
                                                className="text-xs bg-red-500/20 text-red-400 hover:bg-red-500/30 px-2 py-1 rounded transition-colors"
                                            >
                                                Supprimer
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-center text-gray-400 py-8">
                                Aucun media pour ce produit
                            </p>
                        )}
                    </div>
                </Modal>
            </div>
        </div>
    );
};

export default Products;
